import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash.debounce';
import 'isomorphic-fetch';
import SvgSprite from '../../../svg-sprite/SvgSprite.jsx';
import searchTheme from './SearchTheme.jsx';
import trackEvent from '../../../../../javascripts/helpers/trackEvent';

/**
 * Whenever react-autosuggest requests a fetch for suggestions this
 * function makes a request to the BE and gets the correct suggestions
 * @param {string} value The user input
 * @param {string} searchAPI Search API (fetch suggestions)
 * @param {function} setSuggestions UseState handler to set the suggestions
 * @returns {void}
 */
const getSuggestions = (value, searchAPI, setSuggestions) => {
  const searchAPIValue = `${searchAPI}=${value}`;
  if (value.length > 2) {
    fetch(searchAPIValue, {
      method: 'get'
    })
      .then(response => {
        return response.json();
      })
      .then(data => setSuggestions(data));
  }
};
const debouncedGetSuggestions = debounce(getSuggestions, 375);

/**
 * Sends the user to the search page
 * @param {string} searchValue Search value
 *  @param {string} actionURL Search URL
 * @param {string} type Search type (e.g. Main, Stage or resultPage)
 * @returns {void}
 */
const goToSearchPage = (searchValue, actionURL, type) => {
  const searchURL = `${actionURL}=${encodeURIComponent(searchValue)}`;
  trackEvent(type, 'submit', searchValue);
  window.location = searchURL;
};

/**
 * When a suggestion is selected by the user, this function
 * either will go to the search page or to the suggestion href
 * @param {Object} suggestion Suggestion object
 * @param {string} suggestionValue Suggestion value
 * @param {string} actionURL Search URL
 * @param {string} type Search type (e.g. Main, Stage or resultPage)
 * @returns {void}
 */
const postSearchValue = (suggestion, suggestionValue, actionURL, type) => {
  if (suggestion.type === 'search' && suggestionValue) {
    goToSearchPage(suggestionValue, actionURL, type);
  } else {
    trackEvent(`${type}_Suggestions`, 'OnClick', suggestion.href);
    window.location = suggestion.href;
  }
};

/**
 * When the user highlights a rendered suggestion,
 * react-autosuggest will update the input value accordingly.
 * @param {Object} suggestion Suggestion object
 * @returns {string} Returns suggestion text
 */
const getSuggestionValue = suggestion => suggestion.text;

/**
 * Tells react-autosuggest how to render a suggestion
 * @param {Object} suggestion Suggestion object
 * @returns {string} Returns html element with suggestion data
 */
const renderSuggestion = suggestion =>
  suggestion.type !== 'search' ? (
    <a className="a-search-auto__suggestion-inner" href={suggestion.href}>
      <span className="a-search-auto__suggestion-text">
        <SvgSprite id={`search-${suggestion.type}`} title={suggestion.type} />
        <span>{suggestion.text}</span>
      </span>
      {suggestion.type === 'page' ? (
        <span className="a-search-auto__suggestion-entry">{suggestion.entry}</span>
      ) : null}
    </a>
  ) : (
    <div className="a-search-auto__suggestion-inner">
      <span className="a-search-auto__suggestion-text">
        <SvgSprite
          id="default-search"
          title="search"
          className="a-svg-sprite--default-search-small"
        />
        <span>{suggestion.text}</span>
      </span>
    </div>
  );

/**
 * React-autosuggest needs this function when multisection is set to true.
 * However in the current state there is no need to render a section title.
 * @returns {void} Returns null
 */
const renderSectionTitle = () => null;

/**
 * React-autosuggest needs to know about sections, that should be rendered.
 * @param {Object} section Section object
 * @returns {array} Returns the suggestions based on the section
 */
const getSectionSuggestions = section => section.suggestions;

const SearchInput = ({ data, type }) => {
  const { placeholder, buttonLabel, searchAPI, actionURL, inputValue } = data;

  const [suggestions, setSuggestions] = useState([]);
  const [searchValue, setSearchValue] = useState(inputValue || '');

  const onKeyPress = e => {
    if (e.key.toLowerCase() === 'enter') goToSearchPage(searchValue, actionURL, type);
  };

  const onChange = (event, { newValue }) => {
    setSearchValue(newValue);

    debounce(() => trackEvent(type, 'onChange', newValue), 375);
  };

  const onSuggestionsFetchRequested = ({ value, reason }) => {
    if (reason !== 'input-focused') debouncedGetSuggestions(value, searchAPI, setSuggestions);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const onSuggestionSelected = (event, { suggestion, suggestionValue }) => {
    postSearchValue(suggestion, suggestionValue, actionURL, type);
  };

  const inputProps = {
    value: searchValue,
    placeholder,
    onChange,
    onKeyPress
  };

  return (
    <div className="a-search-auto__wrapper">
      <SvgSprite id="default-search" title="search" />
      <Autosuggest
        multiSection
        theme={searchTheme}
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        getSectionSuggestions={getSectionSuggestions}
        inputProps={inputProps}
      />
      <button
        type="button"
        className="a-button a-button--primary-grey a-search-auto__button"
        onClick={() => goToSearchPage(searchValue, actionURL, type)}
        onKeyPress={onKeyPress}
      >
        {buttonLabel}
      </button>
    </div>
  );
};

SearchInput.propTypes = {
  data: PropTypes.shape({
    searchAPI: PropTypes.string,
    actionURL: PropTypes.string,
    placeholder: PropTypes.string,
    buttonLabel: PropTypes.string,
    inputValue: PropTypes.string
  }).isRequired,
  type: PropTypes.string.isRequired
};

export default SearchInput;
